/**
 * Schedule collaboration modals — mirrors AcknowledgmentWorkflowModals UX exactly.
 *
 * Six per-row action modals (Accept / Decline / Propose Date / Propose Qty / Hold / Release).
 * Each modal owns its own form state, and reports back via onSubmit(payload).
 * Bulk modals are re-exported from ./ScheduleBulkModals.
 */
import { useState } from 'react';
import { X, CheckCircle, XCircle, Calendar, Pause, Play } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../../ui/card';
import { ReasonCodeSelect, getEffectiveReason } from '../../AcknowledgmentSharedUtils';
import type { POLineSchedule } from '../../../../lib/api/types/purchasing';
import { useFormatters } from '../../../../contexts/PreferencesContext';

export type ModalType =
  | { kind: 'accept'; schedule: POLineSchedule }
  | { kind: 'decline'; schedule: POLineSchedule }
  | { kind: 'propose_date'; schedule: POLineSchedule }
  | { kind: 'propose_qty'; schedule: POLineSchedule }
  | { kind: 'hold'; schedule: POLineSchedule }
  | { kind: 'release'; schedule: POLineSchedule }
  // Close the remainder of a manually agreed (collab-held) line — customer only.
  | { kind: 'close_held'; schedule: POLineSchedule }
  | null;

export interface ScheduleActionPayload {
  action: 'accept' | 'decline' | 'propose_date' | 'propose_qty' | 'hold' | 'release';
  scheduleId: number;
  comments?: string;
  reason?: string;
  proposedDate?: string;
  proposedQty?: number;
}

interface BaseModalProps {
  isOpen: boolean;
  schedule: POLineSchedule | null;
  onClose: () => void;
  submitting: boolean;
  onSubmit: (data: ScheduleActionPayload) => Promise<void>;
}

// ----- Shared info panel -----

type Accent = 'green' | 'red' | 'orange';

const ACCENT_STYLES: Record<Accent, { bg: string; border: string; text: string }> = {
  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
  red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700' },
};

function fmtDate(
  d: string | null | undefined,
  fmt?: (x: string | Date) => string, // CR-6307: preference-aware formatter
): string {
  if (!d) return '-';
  if (fmt) {
    try { return fmt(d); } catch { return d; }
  }
  try {
    // eslint-disable-next-line no-restricted-syntax -- CR-6307: no-formatter fallback only
    return new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return d;
  }
}

function ScheduleInfoPanel({
  schedule,
  accent,
  showBalance = false,
}: {
  schedule: POLineSchedule;
  accent: Accent;
  showBalance?: boolean;
}) {
  const { formatDate } = useFormatters();
  const s = ACCENT_STYLES[accent];
  return (
    <div className={`${s.bg} ${s.border} border rounded-lg p-3 space-y-2`}>
      <div className="flex justify-between text-sm">
        <span className="font-medium">PO Number:</span>
        <span className="font-mono">{schedule.po_number}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="font-medium">PO Line #:</span>
        <span>{schedule.line_number}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="font-medium">Schedule Line #:</span>
        <span>{schedule.schedule_line_number || '-'}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="font-medium">Part Number:</span>
        <span className="font-mono">{schedule.part_number}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="font-medium">Supplier:</span>
        <span>{schedule.supplier_name}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="font-medium">Schedule Date:</span>
        <span className={`font-medium ${s.text}`}>{fmtDate(schedule.schedule_date, formatDate)}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="font-medium">Schedule Qty:</span>
        <span>{schedule.schedule_qty}</span>
      </div>
      {showBalance && (
        <div className="flex justify-between text-sm">
          <span className="font-medium">Balance Qty:</span>
          <span>{schedule.balance_qty}</span>
        </div>
      )}
      <PendingProposalRows schedule={schedule} />
    </div>
  );
}

/**
 * The actual change being accepted / declined (customer review 2026-07-08).
 * When a date/qty change is in flight, the Accept modal previously showed
 * only the ORIGINAL schedule values, so the reviewer couldn't see what they
 * were agreeing to. Both sides' proposals are stored in
 * supplier_proposed_date / supplier_proposed_qty by the workflow service.
 */
function PendingProposalRows({ schedule }: { schedule: POLineSchedule }) {
  const { formatDate } = useFormatters();
  const proposalPending =
    schedule.acknowledge_status === 'change_proposed_supplier' ||
    schedule.acknowledge_status === 'change_proposed_customer';
  if (!proposalPending) return null;
  const proposedBy = schedule.acknowledge_status === 'change_proposed_supplier' ? 'Supplier' : 'Customer';
  const hasDate = !!schedule.supplier_proposed_date;
  const hasQty = schedule.supplier_proposed_qty !== null && schedule.supplier_proposed_qty !== undefined;
  if (!hasDate && !hasQty) return null;
  return (
    <div className="border-t border-amber-300 mt-2 pt-2 space-y-1 bg-amber-50 -mx-3 -mb-3 px-3 pb-3 rounded-b-lg">
      <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
        Proposed change ({proposedBy})
      </div>
      {hasDate && (
        <div className="flex justify-between text-sm">
          <span className="font-medium">Proposed New Date:</span>
          <span className="font-semibold text-amber-800">{fmtDate(schedule.supplier_proposed_date as string, formatDate)}</span>
        </div>
      )}
      {hasQty && (
        <div className="flex justify-between text-sm">
          <span className="font-medium">Proposed New Qty:</span>
          <span className="font-semibold text-amber-800">{schedule.supplier_proposed_qty}</span>
        </div>
      )}
    </div>
  );
}

function CommentsField({ value, onChange, placeholder = 'Any additional comments...' }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">Comments (Optional)</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
    </div>
  );
}

function FooterButtons({ submitting, onClose, submitColor, icon, idleLabel, busyLabel }: { submitting: boolean; onClose: () => void; submitColor: 'green' | 'red' | 'orange'; icon: React.ReactNode; idleLabel: string; busyLabel: string }) {
  const colorClass =
    submitColor === 'green' ? 'bg-green-600 hover:bg-green-700'
    : submitColor === 'red' ? 'bg-red-600 hover:bg-red-700'
    : 'bg-orange-600 hover:bg-orange-700';
  return (
    <div className="flex justify-end gap-3 pt-2">
      <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 border rounded-lg hover:bg-muted disabled:opacity-50">
        Cancel
      </button>
      <button type="submit" disabled={submitting} className={`px-4 py-2 ${colorClass} text-white rounded-lg disabled:opacity-50 flex items-center gap-2`}>
        {icon}
        {submitting ? busyLabel : idleLabel}
      </button>
    </div>
  );
}

function ModalShell({
  title,
  icon,
  onClose,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            {icon}
            <h3 className="text-lg font-semibold">{title}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-full transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>
        <CardContent className="p-4">{children}</CardContent>
      </Card>
    </div>
  );
}

// ----- Accept -----

export function AcceptScheduleModal({ isOpen, schedule, onClose, submitting, onSubmit }: BaseModalProps) {
  const [comments, setComments] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schedule) return;
    try {
      await onSubmit({ action: 'accept', scheduleId: schedule.id, comments: comments || undefined });
      setComments('');
    } catch (err) {
      console.error('Failed to accept schedule:', err);
    }
  };

  if (!isOpen || !schedule) return null;

  return (
    <ModalShell
      title="Accept Schedule"
      icon={<CheckCircle className="h-5 w-5 text-green-600" />}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <ScheduleInfoPanel schedule={schedule} accent="green" />
        <CommentsField value={comments} onChange={setComments} />
        <FooterButtons
          submitting={submitting}
          onClose={onClose}
          submitColor="green"
          icon={<CheckCircle className="h-4 w-4" />}
          // "Accept Date" misled when the pending proposal was a qty change
          idleLabel={
            schedule.acknowledge_status === 'change_proposed_supplier' || schedule.acknowledge_status === 'change_proposed_customer'
              ? 'Accept Change'
              : 'Accept Date'
          }
          busyLabel="Accepting..."
        />
      </form>
    </ModalShell>
  );
}

// ----- Decline -----

export function DeclineScheduleModal({ isOpen, schedule, onClose, submitting, onSubmit }: BaseModalProps) {
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [comments, setComments] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schedule) return;
    const effectiveReason = getEffectiveReason(reasonSelect, customReason);
    if (!effectiveReason) {
      toast.error('Please provide a reason for declining');
      return;
    }
    try {
      await onSubmit({
        action: 'decline',
        scheduleId: schedule.id,
        reason: effectiveReason,
        comments: comments || undefined,
      });
      setReasonSelect(''); setCustomReason(''); setComments('');
    } catch (err) {
      console.error('Failed to decline schedule:', err);
    }
  };

  if (!isOpen || !schedule) return null;

  return (
    <ModalShell
      title="Decline Schedule"
      icon={<XCircle className="h-5 w-5 text-red-600" />}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <ScheduleInfoPanel schedule={schedule} accent="red" />
        <ReasonCodeSelect
          value={reasonSelect}
          onChange={setReasonSelect}
          customValue={customReason}
          onCustomChange={setCustomReason}
          required
          label="Reason for Decline"
        />
        <CommentsField value={comments} onChange={setComments} placeholder="Provide additional details..." />
        <FooterButtons submitting={submitting} onClose={onClose} submitColor="red" icon={<XCircle className="h-4 w-4" />} idleLabel="Decline Date" busyLabel="Declining..." />
      </form>
    </ModalShell>
  );
}

// ----- Propose Date -----

export function ProposeScheduleDateModal({ isOpen, schedule, onClose, submitting, onSubmit }: BaseModalProps) {
  const [proposedDate, setProposedDate] = useState('');
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [comments, setComments] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schedule) return;
    if (!proposedDate) { toast.error('Please select a proposed date'); return; }
    const effectiveReason = getEffectiveReason(reasonSelect, customReason);
    if (!effectiveReason) { toast.error('Please provide a reason for the change'); return; }
    try {
      await onSubmit({
        action: 'propose_date',
        scheduleId: schedule.id,
        proposedDate,
        reason: effectiveReason,
        comments: comments || undefined,
      });
      setProposedDate(''); setReasonSelect(''); setCustomReason(''); setComments('');
    } catch (err) {
      console.error('Failed to propose date:', err);
    }
  };

  if (!isOpen || !schedule) return null;

  return (
    <ModalShell
      title="Propose Alternative Date"
      icon={<Calendar className="h-5 w-5 text-orange-600" />}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <ScheduleInfoPanel schedule={schedule} accent="orange" />
        <div>
          <label className="block text-sm font-medium mb-2">
            Proposed Promise Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={proposedDate}
            onChange={(e) => setProposedDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            required
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <ReasonCodeSelect
          value={reasonSelect}
          onChange={setReasonSelect}
          customValue={customReason}
          onCustomChange={setCustomReason}
          required
          label="Reason for Change"
        />
        <CommentsField value={comments} onChange={setComments} placeholder="Provide additional details..." />
        <FooterButtons submitting={submitting} onClose={onClose} submitColor="orange" icon={<Calendar className="h-4 w-4" />} idleLabel="Propose Date" busyLabel="Proposing..." />
      </form>
    </ModalShell>
  );
}

// ----- Propose Qty -----

export function ProposeScheduleQtyModal({ isOpen, schedule, onClose, submitting, onSubmit }: BaseModalProps) {
  const [proposedQty, setProposedQty] = useState('');
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [comments, setComments] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schedule) return;
    const qtyNum = Number(proposedQty);
    if (!proposedQty || !Number.isFinite(qtyNum) || qtyNum <= 0) {
      toast.error('Please enter a valid proposed quantity');
      return;
    }
    const effectiveReason = getEffectiveReason(reasonSelect, customReason);
    if (!effectiveReason) { toast.error('Please provide a reason for the change'); return; }
    try {
      await onSubmit({
        action: 'propose_qty',
        scheduleId: schedule.id,
        proposedQty: qtyNum,
        reason: effectiveReason,
        comments: comments || undefined,
      });
      setProposedQty(''); setReasonSelect(''); setCustomReason(''); setComments('');
    } catch (err) {
      console.error('Failed to propose qty:', err);
    }
  };

  if (!isOpen || !schedule) return null;

  return (
    <ModalShell
      title="Propose Quantity Change"
      icon={<Calendar className="h-5 w-5 text-orange-600" />}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <ScheduleInfoPanel schedule={schedule} accent="orange" showBalance />
        <div>
          <label className="block text-sm font-medium mb-2">
            Proposed Quantity <span className="text-red-500">*</span>
          </label>
          {/* No max cap: suppliers may propose a HIGHER qty than the current
              schedule (e.g. to meet a MOQ / batch quantity). The backend
              enforces the parent PO-line quantity as the real ceiling. */}
          <input
            type="number"
            min="0.001"
            step="0.001"
            value={proposedQty}
            onChange={(e) => setProposedQty(e.target.value)}
            required
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <ReasonCodeSelect
          value={reasonSelect}
          onChange={setReasonSelect}
          customValue={customReason}
          onCustomChange={setCustomReason}
          required
          label="Reason for Change"
        />
        <CommentsField value={comments} onChange={setComments} placeholder="Provide additional details..." />
        <FooterButtons submitting={submitting} onClose={onClose} submitColor="orange" icon={<Calendar className="h-4 w-4" />} idleLabel="Propose Qty" busyLabel="Proposing..." />
      </form>
    </ModalShell>
  );
}

// ----- Hold -----

export function HoldScheduleModal({ isOpen, schedule, onClose, submitting, onSubmit }: BaseModalProps) {
  // Customer review 2026-07-08 (Vaida): hold reasons come from the
  // admin-configurable reason-code list (category 'hold', managed in Admin
  // Setup -> Purchasing -> Reason Codes) with an Other (custom) free-text
  // option — replacing the previous bare textarea.
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schedule) return;
    const effectiveReason = getEffectiveReason(reasonSelect, customReason);
    if (!effectiveReason) { toast.error('Reason is required'); return; }
    try {
      await onSubmit({ action: 'hold', scheduleId: schedule.id, reason: effectiveReason });
      setReasonSelect('');
      setCustomReason('');
    } catch (err) {
      console.error('Failed to put schedule on hold:', err);
    }
  };

  if (!isOpen || !schedule) return null;

  return (
    <ModalShell
      title="Put Schedule On Hold"
      icon={<Pause className="h-5 w-5 text-orange-600" />}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <ScheduleInfoPanel schedule={schedule} accent="orange" />
        <ReasonCodeSelect
          value={reasonSelect}
          onChange={setReasonSelect}
          customValue={customReason}
          onCustomChange={setCustomReason}
          required
          label="Reason"
          categories={['hold']}
        />
        <FooterButtons submitting={submitting} onClose={onClose} submitColor="orange" icon={<Pause className="h-4 w-4" />} idleLabel="Put On Hold" busyLabel="Putting On Hold..." />
      </form>
    </ModalShell>
  );
}

// ----- Release -----

export function ReleaseScheduleModal({ isOpen, schedule, onClose, submitting, onSubmit }: BaseModalProps) {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schedule) return;
    try {
      await onSubmit({ action: 'release', scheduleId: schedule.id });
    } catch (err) {
      console.error('Failed to release hold:', err);
    }
  };

  if (!isOpen || !schedule) return null;

  return (
    <ModalShell
      title="Release Hold"
      icon={<Play className="h-5 w-5 text-green-600" />}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <ScheduleInfoPanel schedule={schedule} accent="green" />
        {schedule.on_hold_reason_text && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm font-medium text-orange-900 mb-1">Current hold reason:</p>
            <p className="text-sm text-orange-800">{schedule.on_hold_reason_text}</p>
          </div>
        )}
        <p className="text-sm text-muted-foreground">
          Are you sure you want to release this schedule from hold?
        </p>
        <FooterButtons submitting={submitting} onClose={onClose} submitColor="green" icon={<Play className="h-4 w-4" />} idleLabel="Release Hold" busyLabel="Releasing..." />
      </form>
    </ModalShell>
  );
}

// Re-export bulk modals for backward compat with existing imports
export { BulkAcceptModal, BulkRejectModal, BulkHoldModal } from './ScheduleBulkModals';
